// training-content.ts — the source of truth for the Training tab.
//
// Training is deliberately CONTENT-ONLY: no API, no database table, no new
// permission scope. Lessons and videos are authored here and ship with the
// bundle, so adding training material is a content edit + deploy — it cannot
// leak tenant data, cannot be edited by a tenant, and adds no attack surface.
//
// HOW TO ADD A LESSON
//   Append a TrainingLesson to TRAINING_LESSONS. `body` is a list of typed
//   blocks (no raw HTML — that's on purpose; it keeps XSS off the table).
//
// HOW TO ADD A VIDEO
//   Append a TrainingVideo to TRAINING_VIDEOS with a Cloudflare Stream or
//   YouTube-nocookie URL. Only those hosts embed (see isEmbeddableVideoUrl);
//   anything else renders as a "watch" link instead of an inline player.

/** One block of lesson copy. Typed blocks instead of HTML by design. */
export type LessonBlock =
  | { kind: "p"; text: string }
  | { kind: "h"; text: string }
  | { kind: "steps"; items: string[] }
  | { kind: "bullets"; items: string[] }
  | { kind: "note"; text: string };

export interface TrainingLesson {
  /** URL slug — /training/<slug>. Lowercase, hyphenated, stable once shipped. */
  slug: string;
  title: string;
  /** One line shown in the lesson list. */
  summary: string;
  /** Must be one of TRAINING_CATEGORIES (unknown values sort to the end). */
  category: string;
  /** Rough read time in minutes, shown as a hint. */
  minutes?: number;
  body: LessonBlock[];
}

export interface TrainingVideo {
  slug: string;
  title: string;
  summary: string;
  category: string;
  /** Display duration as free text, e.g. "6:12". */
  duration?: string;
  /** Cloudflare Stream or youtube-nocookie URL. */
  url: string;
}

/** Display order for grouping. Anything not listed sorts after these. */
export const TRAINING_CATEGORIES: string[] = [
  "Getting started",
  "Clients & intake",
  "Credit reports",
  "Qualification",
  "Lenders & packets",
  "Contracts",
  "Billing",
  "Team & permissions",
];

// ---------------------------------------------------------------------------
// CONTENT
// ---------------------------------------------------------------------------
// Empty on purpose. The written lessons and recorded videos get added here.
// The Training tab renders proper empty states until then.

export const TRAINING_LESSONS: TrainingLesson[] = [];

export const TRAINING_VIDEOS: TrainingVideo[] = [];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Only these hosts are allowed to render in an <iframe>. */
const EMBED_HOSTS = [
  /^customer-[a-z0-9]+\.cloudflarestream\.com$/i,
  /^iframe\.cloudflarestream\.com$/i,
  /^www\.youtube-nocookie\.com$/i,
];

/** True when the URL is https and on an allowlisted embed host. */
export function isEmbeddableVideoUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:") return false;
    return EMBED_HOSTS.some((re) => re.test(u.hostname));
  } catch {
    return false;
  }
}

/** Group items by category, honouring TRAINING_CATEGORIES order. */
export function groupByCategory<T extends { category: string }>(items: T[]): Array<[string, T[]]> {
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const key = item.category || "Other";
    const list = buckets.get(key);
    if (list) list.push(item);
    else buckets.set(key, [item]);
  }
  const rank = (name: string) => {
    const i = TRAINING_CATEGORIES.indexOf(name);
    return i === -1 ? TRAINING_CATEGORIES.length : i;
  };
  return [...buckets.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]));
}
